"use client";

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
    Sheet,
    SheetContent,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import { useMemoryDialog } from "@/contexts/memory-dialog-context";
import type { Chat } from "@/db/schema";
import { fetcher } from "@/lib/utils";
import { isToday, isYesterday, subMonths, subWeeks } from "date-fns";
import { motion } from "framer-motion";
import { FileIcon, LoaderIcon, PlusIcon, Trash2Icon } from "lucide-react";
import type { User } from "next-auth";
import Link from "next/link";
import { useParams, usePathname, useRouter } from "next/navigation";
import { useState } from "react";
import { toast } from "sonner";
import useSWRInfinite from "swr/infinite";

import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

type GroupedChats = {
    today: Chat[];
    yesterday: Chat[];
    lastWeek: Chat[];
    lastMonth: Chat[];
    older: Chat[];
};

export interface ChatHistory {
    chats: Array<Chat>;
    hasMore: boolean;
}

const PAGE_SIZE = 20;

const groupChatsByDate = (chats: Chat[]): GroupedChats => {
    const now = new Date();
    const oneWeekAgo = subWeeks(now, 1);
    const oneMonthAgo = subMonths(now, 1);

    return chats.reduce(
        (groups, chat) => {
            const chatDate = new Date(chat.created_at);

            if (isToday(chatDate)) {
                groups.today.push(chat);
            } else if (isYesterday(chatDate)) {
                groups.yesterday.push(chat);
            } else if (chatDate > oneWeekAgo) {
                groups.lastWeek.push(chat);
            } else if (chatDate > oneMonthAgo) {
                groups.lastMonth.push(chat);
            } else {
                groups.older.push(chat);
            }

            return groups;
        },
        {
            today: [],
            yesterday: [],
            lastWeek: [],
            lastMonth: [],
            older: [],
        } as GroupedChats,
    );
};

export function getChatHistoryPaginationKey(
    pageIndex: number,
    previousPageData: ChatHistory,
) {
    if (previousPageData && previousPageData.hasMore === false) {
        return null;
    }

    if (pageIndex === 0) return `/api/history?limit=${PAGE_SIZE}`;

    const firstChatFromPage = previousPageData.chats.at(-1);

    if (!firstChatFromPage) return null;

    return `/api/history?ending_before=${firstChatFromPage.id}&limit=${PAGE_SIZE}`;
}

interface ChatSidebarProps {
    user: User | undefined;
    trigger: React.ReactNode;
}

function ChatItem({
    chat,
    isActive,
    onDelete,
    onNavigate,
}: {
    chat: Chat;
    isActive: boolean;
    onDelete: (chatId: string) => void;
    onNavigate: () => void;
}) {
    return (
        <div
            className={`group flex items-center justify-between px-2 py-2 rounded-lg cursor-pointer transition-colors ${
                isActive
                    ? "bg-muted text-foreground"
                    : "hover:bg-muted/50 text-muted-foreground hover:text-foreground"
            }`}
        >
            <Link
                href={`/chat/${chat.id}`}
                onClick={onNavigate}
                className="flex-1 truncate text-sm"
            >
                {chat.title || "New Chat"}
            </Link>
            <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete(chat.id);
                }}
            >
                <Trash2Icon size={14} />
            </Button>
        </div>
    );
}

export function ChatSidebar({ user, trigger }: ChatSidebarProps) {
    const router = useRouter();
    const pathname = usePathname();
    const { id } = useParams();
    const [isOpen, setIsOpen] = useState(false);
    const { openMemoryDialog } = useMemoryDialog();
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const {
        data: paginatedChatHistories,
        setSize,
        isValidating,
        isLoading,
        mutate,
    } = useSWRInfinite<ChatHistory>(getChatHistoryPaginationKey, fetcher, {
        fallbackData: [],
    });

    // Only show sidebar on chat routes
    const isChatRoute = pathname?.startsWith("/chat");

    if (!isChatRoute) {
        return null;
    }

    const hasReachedEnd = paginatedChatHistories
        ? paginatedChatHistories.some((page) => page.hasMore === false)
        : false;

    const hasEmptyChatHistory = paginatedChatHistories
        ? paginatedChatHistories.every((page) => page.chats.length === 0)
        : false;

    const handleDelete = async () => {
        const deletePromise = fetch(`/api/chat?id=${deleteId}`, {
            method: "DELETE",
        });

        toast.promise(deletePromise, {
            loading: "Deleting chat...",
            success: () => {
                mutate((chatHistories) => {
                    if (chatHistories) {
                        return chatHistories.map((chatHistory) => ({
                            ...chatHistory,
                            chats: chatHistory.chats.filter(
                                (chat) => chat.id !== deleteId,
                            ),
                        }));
                    }
                });

                return "Chat deleted successfully";
            },
            error: "Failed to delete chat",
        });

        setShowDeleteDialog(false);

        if (deleteId === id) {
            router.push("/chat");
        }
    };

    const renderChatHistory = () => {
        if (!user) {
            return (
                <div className="px-4 py-8 text-center">
                    <p className="text-sm text-muted-foreground">
                        Login to save and revisit previous chats!
                    </p>
                </div>
            );
        }

        if (isLoading) {
            return (
                <div className="px-4 py-4">
                    <div className="text-xs text-muted-foreground mb-2">
                        Today
                    </div>
                    <div className="space-y-2">
                        {[44, 32, 28, 64, 52].map((width, index) => (
                            <div
                                key={index}
                                className="h-8 bg-muted/30 rounded animate-pulse"
                                style={{ width: `${width}%` }}
                            />
                        ))}
                    </div>
                </div>
            );
        }

        if (hasEmptyChatHistory) {
            return (
                <div className="px-4 py-8 text-center">
                    <p className="text-sm text-muted-foreground">
                        Your conversations will appear here once you start
                        chatting!
                    </p>
                </div>
            );
        }

        const chatsFromHistory =
            paginatedChatHistories?.flatMap(
                (paginatedChatHistory) => paginatedChatHistory.chats,
            ) || [];

        const groupedChats = groupChatsByDate(chatsFromHistory);

        return (
            <div className="px-4 py-4 space-y-6">
                {groupedChats.today.length > 0 && (
                    <div>
                        <div className="text-xs text-muted-foreground mb-2">
                            Today
                        </div>
                        <div className="space-y-1">
                            {groupedChats.today.map((chat) => (
                                <ChatItem
                                    key={chat.id}
                                    chat={chat}
                                    isActive={chat.id === id}
                                    onDelete={(chatId) => {
                                        setDeleteId(chatId);
                                        setShowDeleteDialog(true);
                                    }}
                                    onNavigate={() => setIsOpen(false)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {groupedChats.yesterday.length > 0 && (
                    <div>
                        <div className="text-xs text-muted-foreground mb-2">
                            Yesterday
                        </div>
                        <div className="space-y-1">
                            {groupedChats.yesterday.map((chat) => (
                                <ChatItem
                                    key={chat.id}
                                    chat={chat}
                                    isActive={chat.id === id}
                                    onDelete={(chatId) => {
                                        setDeleteId(chatId);
                                        setShowDeleteDialog(true);
                                    }}
                                    onNavigate={() => setIsOpen(false)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {groupedChats.lastWeek.length > 0 && (
                    <div>
                        <div className="text-xs text-muted-foreground mb-2">
                            Last 7 days
                        </div>
                        <div className="space-y-1">
                            {groupedChats.lastWeek.map((chat) => (
                                <ChatItem
                                    key={chat.id}
                                    chat={chat}
                                    isActive={chat.id === id}
                                    onDelete={(chatId) => {
                                        setDeleteId(chatId);
                                        setShowDeleteDialog(true);
                                    }}
                                    onNavigate={() => setIsOpen(false)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {groupedChats.lastMonth.length > 0 && (
                    <div>
                        <div className="text-xs text-muted-foreground mb-2">
                            Last 30 days
                        </div>
                        <div className="space-y-1">
                            {groupedChats.lastMonth.map((chat) => (
                                <ChatItem
                                    key={chat.id}
                                    chat={chat}
                                    isActive={chat.id === id}
                                    onDelete={(chatId) => {
                                        setDeleteId(chatId);
                                        setShowDeleteDialog(true);
                                    }}
                                    onNavigate={() => setIsOpen(false)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {groupedChats.older.length > 0 && (
                    <div>
                        <div className="text-xs text-muted-foreground mb-2">
                            Older than last month
                        </div>
                        <div className="space-y-1">
                            {groupedChats.older.map((chat) => (
                                <ChatItem
                                    key={chat.id}
                                    chat={chat}
                                    isActive={chat.id === id}
                                    onDelete={(chatId) => {
                                        setDeleteId(chatId);
                                        setShowDeleteDialog(true);
                                    }}
                                    onNavigate={() => setIsOpen(false)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                <motion.div
                    onViewportEnter={() => {
                        if (!isValidating && !hasReachedEnd) {
                            setSize((size) => size + 1);
                        }
                    }}
                />

                {hasReachedEnd ? (
                    <div className="text-center py-4">
                        <p className="text-xs text-muted-foreground">
                            You have reached the end of your chat history.
                        </p>
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-2 py-4">
                        <div className="animate-spin">
                            <LoaderIcon size={16} />
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Loading Chats...
                        </p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>{trigger}</SheetTrigger>
                <SheetTitle className="sr-only">Chat History</SheetTitle>
                <SheetContent
                    side="left"
                    className="w-[280px] p-0 flex flex-col"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-4 border-b">
                        <Link
                            href="/chat"
                            onClick={() => setIsOpen(false)}
                            className="flex-1 overflow-hidden whitespace-nowrap"
                        >
                            <span className="text-lg font-semibold hover:bg-muted rounded-md cursor-pointer px-1 -ml-1">
                                Chat History
                            </span>
                        </Link>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto">
                        {renderChatHistory()}
                    </div>

                    {/* Footer */}
                    <div className="p-4 space-y-2 border-t">
                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => {
                                router.push("/chat");
                                setIsOpen(false);
                            }}
                        >
                            <div className="flex flex-row gap-3 items-center">
                                <PlusIcon size={18} />
                                New Chat
                            </div>
                        </Button>
                        <Button
                            variant="outline"
                            type="button"
                            className="w-full"
                            onClick={() => {
                                openMemoryDialog();
                                setIsOpen(false);
                            }}
                        >
                            <div className="flex flex-row gap-3 items-center">
                                <FileIcon size={18} />
                                Memory Bank
                            </div>
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>

            <AlertDialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Are you absolutely sure?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. This will permanently
                            delete your chat and remove it from our servers.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete}>
                            Continue
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
